import { Spinner } from '../components/feedback';
import { OperationBoundary } from '../components/OperationBoundary';
import { EditBoundary, useEditDirty } from '../components/EditBoundary';
// リソース（CPU・メモリ・ヒープ・データディスク）の変更。変更は承認を経てジョブで行う。
import { HeapFields } from '../components/HeapFields';
import { LaunchSettingsTab } from './LaunchSettingsTab';
import { useState } from 'react';
import { api } from '../api/client';
import { apiM } from '../api/client-maint';
import type { ChangeRequest, RequestKind, ServerView } from '../api/types';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert, Meter } from '../components/ui';
import { formatGb } from '../format';
import { usePoll } from '../hooks/usePoll';
import { PendingForServer, ReviewDialog } from './ServerManage';

const toGb = (mb: number) => mb / 1024;

function DiskCard({ server }: { server: ServerView }) {
  const storage = usePoll(() => apiM.storage(server.id), 30000, [server.id, server.resources.dataDiskGb]);
  const disk = storage.data;
  const total = disk?.filesystemGb ?? server.resources.dataDiskGb;
  const used = disk?.usedGb;
  const ratio = used !== undefined && total > 0 ? used / total : undefined;
  return (
    <section className="card card-body stack" style={{ gap: 10 }}>
      <h3>データディスク</h3>
      <ErrorAlert error={storage.error} />
      <Meter
        label="使用量"
        value={used ?? 0}
        max={total}
        detail={`${used !== undefined ? `${used.toFixed(1)}GB` : '—'} / ${total}GB`}
      />
      {ratio !== undefined && ratio >= 0.8 && (
        <div className={`alert ${ratio >= 0.9 ? 'danger' : 'warn'} small`}>
          残りが少なくなっています（{Math.round(ratio * 100)}% 使用）。下の「データディスク」を大きくしてください。
          拡張はサーバーを止めずにできます。
        </div>
      )}
      <dl className="small" style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '4px 12px' }}>
        <dt className="faint">割り当て</dt>
        <dd style={{ margin: 0 }}>{server.resources.dataDiskGb}GB</dd>
        <dt className="faint">ノードの空き</dt>
        <dd style={{ margin: 0 }}>{disk?.nodeFreeGb !== undefined ? `${disk.nodeFreeGb}GB（${server.node}）` : '—'}</dd>
        {disk?.replicaFreeGb !== undefined && (
          <>
            <dt className="faint">複製先の空き</dt>
            <dd style={{ margin: 0 }}>{disk.replicaFreeGb}GB</dd>
          </>
        )}
        {disk?.maxGb !== undefined && (
          <>
            <dt className="faint">広げられる上限</dt>
            <dd style={{ margin: 0 }}>{disk.maxGb}GB</dd>
          </>
        )}
      </dl>
    </section>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  unit,
  hint,
  error,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  unit: string;
  hint?: string;
  error?: string;
}) {
  return (
    <div className="field" data-search-id={id}>
      <label htmlFor={id}>{label}</label>
      <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
        <input
          id={id}
          className="input"
          type="number"
          min={min}
          max={max}
          step={step}
          value={Number.isFinite(value) ? value : ''}
          onChange={(e) => onChange(Number(e.target.value))}
          style={{ width: 110 }}
          aria-invalid={Boolean(error)}
          aria-describedby={error ? `${id}-error` : undefined}
        />
        <span className="small muted">{unit}</span>
      </div>
      {hint && <div className="small faint">{hint}</div>}
      {error && (
        <div id={`${id}-error`} className="small" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </div>
      )}
    </div>
  );
}

function ResourcesTabContent({ server }: { server: ServerView }) {
  const { canOperate, notify } = useApp();
  const nodes = usePoll(() => api.nodes(), 30000, [server.id]);
  const [targetNode, setTargetNode] = useState('');
  const [address, setAddress] = useState('');
  const current = server.resources;
  const [cores, setCores] = useState(current.cores);
  const [memoryGb, setMemoryGb] = useState(toGb(current.memoryMb));
  const [heap, setHeap] = useState({ heapMb: current.heapMb, heapMinMb: current.heapMinMb ?? current.heapMb });
  const [diskGb, setDiskGb] = useState(current.dataDiskGb);
  const [request, setRequest] = useState<ChangeRequest>();
  const [busy, setBusy] = useState(false);

  const target = {
    cores,
    memoryMb: Math.round(memoryGb * 1024),
    ...heap,
    dataDiskGb: diskGb,
  };
  const params = Object.fromEntries(
    (Object.keys(target) as Array<keyof typeof target>)
      .filter((key) => target[key] !== (key === 'heapMinMb' ? (current.heapMinMb ?? current.heapMb) : current[key]))
      .map((key) => [key, target[key]]),
  );
  useEditDirty(Object.keys(params).length > 0 || targetNode !== '' || address !== '');
  const vmRestart = target.cores !== current.cores || target.memoryMb !== current.memoryMb;
  const mcRestart = !vmRestart && (target.heapMb !== current.heapMb || target.heapMinMb !== current.heapMinMb);
  const errors = {
    cores: !Number.isInteger(cores) || cores < 1 || cores > 32 ? '1〜32 コアの整数を入力してください' : undefined,
    memory: !Number.isFinite(memoryGb) || memoryGb < 1 || memoryGb > 64 ? '1〜64 GB を入力してください' : undefined,
    heap:
      target.memoryMb - target.heapMb < 1024
        ? 'ヒープは VM のメモリより小さくし、OS の分として 1GB 以上残してください'
        : !Number.isFinite(heap.heapMb) || heap.heapMinMb < 512 || heap.heapMinMb > heap.heapMb || heap.heapMb > 61440
          ? '0.5〜60 GB を入力してください'
          : undefined,
    disk:
      !Number.isInteger(diskGb) || diskGb < current.dataDiskGb || diskGb > 4096
        ? `現在の容量以上、4096 GB 以下の整数を入力してください（データディスクは小さくできません）`
        : undefined,
  };
  const problems = Object.values(errors).filter(Boolean);

  const plan = async () => {
    setBusy(true);
    try {
      setRequest(await api.createRequest('change_resources' as RequestKind, params, server.id));
    } catch (error) {
      notify(errorMessage(error), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="stack" style={{ maxWidth: 820 }}>
      <PendingForServer serverId={server.id} />
      {canOperate && (
        <section className="card card-body stack">
          <h3 data-search-id="move-node">ノードを移す</h3>
          <p className="small muted">
            現在: {server.node}。自動スナップショットを作成し、VM
            を停止してディスクごと移します。稼働中なら移動後に起動し直し、停止中なら停止を維持します。
          </p>
          <ErrorAlert error={nodes.error} />
          <select
            aria-label="移動先ノード"
            className="input"
            value={targetNode}
            onChange={(event) => setTargetNode(event.target.value)}
          >
            <option value="">移動先を選ぶ</option>
            {nodes.data
              ?.filter((node) => node.online && node.role !== 'infra' && node.name !== server.node)
              .map((node) => (
                <option key={node.name} value={node.name}>
                  {node.name}
                </option>
              ))}
          </select>
          <button
            className="btn"
            disabled={busy || !targetNode}
            onClick={async () => {
              setBusy(true);
              try {
                setRequest(await api.createRequest('move_node', { node: targetNode }, server.id));
              } catch (error) {
                notify(errorMessage(error), true);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy && <Spinner />}
            移動の計画を確認
          </button>
        </section>
      )}
      <section className="card card-body stack">
        <h3>ワールド VM の固定 IP</h3>
        <p className="small muted">
          現在: {server.worldNetwork?.address ?? 'DHCP'}。 移行すると VM と Minecraft
          を起動し直します。完了後は接続先の「edge に反映」を実行してください。
        </p>
        {canOperate && (
          <>
            <label htmlFor="world-ip">固定 IP（空欄なら空きを自動選択）</label>
            <input
              data-search-id="world-ip"
              id="world-ip"
              className="input"
              value={address}
              placeholder="192.168.10.140"
              onChange={(e) => setAddress(e.target.value)}
            />
            <button
              className="btn"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  setRequest(await api.createRequest('change_world_ip', address ? { address } : {}, server.id));
                } catch (error) {
                  notify(errorMessage(error), true);
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy && <Spinner />}
              固定 IP への移行を確認
            </button>
          </>
        )}
      </section>
      <div className="grid grid-nodes">
        <section className="card card-body stack" style={{ gap: 6 }}>
          <h3>現在</h3>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            {current.cores} コア ・ {formatGb(current.memoryMb)}
          </div>
          <div className="small muted">
            ヒープ（最小）{formatGb(current.heapMinMb)} ・ ヒープ（最大）{formatGb(current.heapMb)} ・ データディスク{' '}
            {current.dataDiskGb}GB
          </div>
        </section>
        <DiskCard server={server} />
      </div>
      <section className="card card-body stack">
        <h2>変更後</h2>
        <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
          <NumberField
            id="res-cores"
            label="CPU"
            value={cores}
            onChange={setCores}
            min={1}
            max={32}
            unit="コア"
            error={errors.cores}
          />
          <NumberField
            id="res-memory"
            label="VM のメモリ"
            value={memoryGb}
            onChange={setMemoryGb}
            min={1}
            max={64}
            step={0.5}
            unit="GB"
            error={errors.memory}
          />
          <NumberField
            id="res-disk"
            label="データディスク"
            value={diskGb}
            onChange={setDiskGb}
            min={current.dataDiskGb}
            max={4096}
            step={10}
            unit="GB"
            hint="大きくするだけ（小さくはできません）"
            error={errors.disk}
          />
        </div>
        <HeapFields value={heap} onChange={setHeap} unit="GB" />
        {errors.heap && (
          <div className="alert danger small" role="alert">
            {errors.heap}
          </div>
        )}
        {Object.keys(params).length > 0 && problems.length === 0 && (
          <div className="alert info small">
            {vmRestart
              ? 'CPU・メモリの変更には VM の再起動が必要です（数十秒〜1 分ほど止まります）。'
              : mcRestart
                ? 'ヒープの変更は Minecraft の再起動で反映します。'
                : 'データディスクの拡張はサーバーを止めずに行います。'}
            {target.dataDiskGb !== current.dataDiskGb && vmRestart && ' データディスクの拡張は止めずに先に行います。'}
          </div>
        )}
        {canOperate && (
          <div className="row">
            <span className="spacer" />
            <button
              className="btn primary"
              disabled={busy || Object.keys(params).length === 0 || problems.length > 0}
              onClick={() => void plan()}
            >
              {busy && <Spinner />}
              変更内容を確認
            </button>
          </div>
        )}
      </section>
      {request && <ReviewDialog request={request} onClose={() => setRequest(undefined)} />}
    </div>
  );
}

export function ResourcesTab({ server }: { server: ServerView }) {
  return (
    <div className="stack">
      <OperationBoundary serverId={server.id}>
        <EditBoundary
          resource={{ target: `server:${server.id}`, screen: 'resources' }}
          revision={String(server.revision)}
        >
          <ResourcesTabContent server={server} />
        </EditBoundary>
      </OperationBoundary>
      <LaunchSettingsTab server={server} />
    </div>
  );
}
